import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = ["title"]
const _hoisted_3 = {
  key: 0,
  class: "animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
}
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = { class: "p-3.5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800/80 border-b border-gray-200/80 dark:border-gray-700 flex items-center justify-between" }
const _hoisted_6 = { class: "flex items-center space-x-2" }
const _hoisted_7 = { class: "text-[10px] text-gray-500 dark:text-gray-400" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "px-3 pt-2.5 pb-2 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between gap-1.5"
}
const _hoisted_10 = { class: "flex items-center gap-1" }
const _hoisted_11 = { class: "max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60" }
const _hoisted_12 = {
  key: 0,
  class: "p-6 text-center space-y-2"
}
const _hoisted_13 = {
  key: 1,
  class: "p-6 text-center space-y-2"
}
const _hoisted_14 = { class: "w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto" }
const _hoisted_15 = { class: "text-xs font-bold text-gray-800 dark:text-gray-200" }
const _hoisted_16 = { class: "text-[11px] text-gray-500 dark:text-gray-400 max-w-xs mx-auto" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "flex-1 min-w-0" }
const _hoisted_19 = { class: "flex items-center justify-between gap-1" }
const _hoisted_20 = { class: "text-xs font-bold text-gray-900 dark:text-white truncate group-hover:text-secondary dark:group-hover:text-secondary-dark-text transition-colors" }
const _hoisted_21 = {
  key: 0,
  class: "text-[11px] text-gray-600 dark:text-gray-300 font-medium truncate mt-0.5 flex items-center gap-1"
}
const _hoisted_22 = {
  key: 0,
  class: "text-gray-400"
}
const _hoisted_23 = { class: "flex items-center justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-1" }
const _hoisted_24 = { class: "capitalize truncate max-w-[140px]" }
const _hoisted_25 = { class: "p-2.5 bg-gray-50 dark:bg-gray-850 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs" }
const _hoisted_26 = { class: "text-[10px] text-gray-400 font-mono" }

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUser } from '@/composables/auth/useUser'
import { useDocumentNotificationStore } from '@/stores/documentNotification'
import type { DocumentExpirationAlert } from '@/types/documentNotification'
import {
  Bell,
  AlertTriangle,
  Clock,
  CheckCircle2,
  RefreshCw,
  Loader2,
  ChevronRight,
  User,
} from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentExpirationNotification',
  setup(__props) {

const router = useRouter()
const authStore = useAuthStore()
const { userRoleLabel } = useUser()
const isStaffOnly = computed(() => !authStore.isAdmin)
const store = useDocumentNotificationStore()

const isOpen = ref(false)
const activeFilter = ref<'all' | 'expired' | 'expiring_soon'>('all')
const triggerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    store.fetchAlerts(true)
  }
}

const closeDropdown = () => {
  isOpen.value = false
}

const badgeCount = computed(() => {
  const count = store.summary.total_alerts
  return count > 99 ? '99+' : count.toString()
})

const tooltipText = computed(() => {
  if (store.summary.total_alerts === 0) {
    return 'Document Expiration Alerts: All documents up to date'
  }
  const parts = []
  if (store.summary.expired_count > 0) {
    parts.push(`${store.summary.expired_count} expired`)
  }
  if (store.summary.expiring_soon_count > 0) {
    parts.push(`${store.summary.expiring_soon_count} expiring soon`)
  }
  return `Document Expirations: ${parts.join(', ')}`
})

const filteredAlerts = computed(() => {
  if (activeFilter.value === 'expired') {
    return store.alerts.filter(a => a.is_expired)
  }
  if (activeFilter.value === 'expiring_soon') {
    return store.alerts.filter(a => !a.is_expired)
  }
  return store.alerts
})

const formatExpiryDescription = (alert: DocumentExpirationAlert): string => {
  if (!alert.expiry_date) return 'No expiry'
  const days = alert.days_until_expiry

  if (alert.is_expired) {
    if (days !== null && days < 0) {
      const pastDays = Math.abs(days)
      if (pastDays === 0) return 'Expired today'
      if (pastDays === 1) return 'Expired yesterday'
      if (pastDays < 30) return `Expired ${pastDays}d ago`
      const months = Math.floor(pastDays / 30)
      return `Expired ~${months} mo ago`
    }
    return `Expired (${alert.expiry_date})`
  }

  // Expiring soon
  if (days !== null) {
    if (days === 0) return 'Expires today'
    if (days === 1) return 'Expires tomorrow'
    if (days < 60) return `Expires in ${days}d`
    const months = Math.floor(days / 30)
    return `Expires in ~${months} months`
  }
  return `Expires: ${alert.expiry_date}`
}

const handleRefresh = async () => {
  await store.fetchAlerts(true)
}

const handleAlertClick = (alert: DocumentExpirationAlert) => {
  closeDropdown()
  if (isStaffOnly.value) {
    router.push({ path: '/my-documents', query: { filter: alert.is_expired ? 'expired' : 'expiring_soon' } })
  } else {
    // Admin / Owner
    if (alert.type === 'business_document') {
      router.push('/business-documents')
    } else {
      router.push({
        path: '/staff',
        query: {
          employee_id: alert.employee_id ? alert.employee_id.toString() : undefined,
          open_docs: 'true',
          initial_filter: alert.is_expired ? 'expired' : 'expiring_soon',
        },
      })
    }
  }
}

const handleNavigateToDocuments = () => {
  closeDropdown()
  if (isStaffOnly.value) {
    const query = activeFilter.value !== 'all' ? { filter: activeFilter.value } : undefined
    router.push({ path: '/my-documents', query })
  } else {
    router.push('/staff')
  }
}

// Click outside handling
const handleDocumentClick = (e: MouseEvent) => {
  if (!isOpen.value) return
  const target = e.target as Node
  if (
    triggerRef.value &&
    !triggerRef.value.contains(target) &&
    dropdownRef.value &&
    !dropdownRef.value.contains(target)
  ) {
    closeDropdown()
  }
}

onMounted(() => {
  store.fetchAlerts()
  document.addEventListener('click', handleDocumentClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      ref_key: "triggerRef",
      ref: triggerRef,
      type: "button",
      class: _normalizeClass(["relative p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-all duration-200", {
        'text-red-600 dark:text-red-400 hover:text-red-700': _unref(store).hasExpired,
        'text-amber-600 dark:text-amber-400 hover:text-amber-700': !_unref(store).hasExpired && _unref(store).hasExpiringSoon,
      }]),
      title: tooltipText.value,
      onClick: toggleDropdown,
      "aria-label": "Document Expiration Notifications"
    }, [
      _createVNode(_unref(Bell), { class: "w-5 h-5 transition-transform group-hover:scale-110" }),
      (_unref(store).hasAlerts)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(["absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] px-1 items-center justify-center rounded-full text-[10px] font-extrabold shadow-sm ring-2 ring-white dark:ring-gray-800", _unref(store).hasExpired ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'])
          }, [
            (_unref(store).hasExpired)
              ? (_openBlock(), _createElementBlock("span", _hoisted_3))
              : _createCommentVNode("", true),
            _createElementVNode("span", _hoisted_4, _toDisplayString(badgeCount.value), 1)
          ], 2))
        : _createCommentVNode("", true)
    ], 10, _hoisted_2),
    _createVNode(_Transition, {
      "enter-active-class": "transition duration-150 ease-out",
      "enter-from-class": "transform scale-95 opacity-0",
      "enter-to-class": "transform scale-100 opacity-100",
      "leave-active-class": "transition duration-100 ease-in",
      "leave-from-class": "transform scale-100 opacity-100",
      "leave-to-class": "transform scale-95 opacity-0"
    }, {
      default: _withCtx(() => [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "dropdownRef",
              ref: dropdownRef,
              class: "absolute right-0 z-50 mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] origin-top-right rounded-2xl bg-white dark:bg-gray-800 py-0 shadow-2xl ring-1 ring-black/5 dark:ring-white/10 border border-gray-200/80 dark:border-gray-700 focus:outline-none overflow-hidden"
            }, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["w-7 h-7 rounded-lg flex items-center justify-center", _unref(store).hasExpired ? 'bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400' : (_unref(store).hasExpiringSoon ? 'bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400')])
                  }, [
                    _createVNode(_unref(Bell), { class: "w-4 h-4" })
                  ], 2),
                  _createElementVNode("div", null, [
                    _cache[3] || (_cache[3] = _createElementVNode("h3", { class: "text-xs font-bold text-gray-900 dark:text-white leading-tight" }, " Document Expiration Alerts ", -1)),
                    _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(userRoleLabel)), 1)
                  ])
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700 transition",
                  title: "Refresh alerts",
                  disabled: _unref(store).isLoading,
                  onClick: _withModifiers(handleRefresh, ["stop"])
                }, [
                  _createVNode(_unref(RefreshCw), {
                    class: _normalizeClass(["w-3.5 h-3.5", { 'animate-spin': _unref(store).isLoading }])
                  }, null, 8, ["class"])
                ], 8, _hoisted_8)
              ]),
              (_unref(store).hasAlerts)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(["px-2.5 py-1 rounded-lg text-[11px] font-bold transition", activeFilter.value === 'all' ? 'bg-secondary text-white shadow-xs' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200/60 dark:hover:bg-gray-700']),
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (activeFilter.value = 'all'))
                      }, " All (" + _toDisplayString(_unref(store).summary.total_alerts) + ") ", 3),
                      (_unref(store).summary.expired_count > 0)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            type: "button",
                            class: _normalizeClass(["px-2 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1", activeFilter.value === 'expired' ? 'bg-red-500 text-white shadow-xs' : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40']),
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (activeFilter.value = 'expired'))
                          }, [
                            _createElementVNode("span", {
                              class: _normalizeClass(["w-1.5 h-1.5 rounded-full bg-red-500", { 'bg-white': activeFilter.value === 'expired' }])
                            }, null, 2),
                            _createElementVNode("span", null, "Expired (" + _toDisplayString(_unref(store).summary.expired_count) + ")", 1)
                          ], 2))
                        : _createCommentVNode("", true),
                      (_unref(store).summary.expiring_soon_count > 0)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 1,
                            type: "button",
                            class: _normalizeClass(["px-2 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1", activeFilter.value === 'expiring_soon' ? 'bg-amber-500 text-white shadow-xs' : 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40']),
                            onClick: _cache[2] || (_cache[2] = ($event: any) => (activeFilter.value = 'expiring_soon'))
                          }, [
                            _createElementVNode("span", {
                              class: _normalizeClass(["w-1.5 h-1.5 rounded-full bg-amber-500", { 'bg-white': activeFilter.value === 'expiring_soon' }])
                            }, null, 2),
                            _createElementVNode("span", null, "Expiring (" + _toDisplayString(_unref(store).summary.expiring_soon_count) + ")", 1)
                          ], 2))
                        : _createCommentVNode("", true)
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_11, [
                (_unref(store).isLoading && !_unref(store).alerts.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                      _createVNode(_unref(Loader2), { class: "w-6 h-6 animate-spin text-secondary mx-auto" }),
                      _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-xs text-gray-500 dark:text-gray-400" }, "Checking document expirations...", -1))
                    ]))
                  : (!filteredAlerts.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                        _createElementVNode("div", _hoisted_14, [
                          _createVNode(_unref(CheckCircle2), { class: "w-6 h-6" })
                        ]),
                        _createElementVNode("p", _hoisted_15, _toDisplayString(activeFilter.value === 'all' ? 'All documents up to date!' : (activeFilter.value === 'expired' ? 'No expired documents.' : 'No documents expiring soon.')), 1),
                        _createElementVNode("p", _hoisted_16, _toDisplayString(activeFilter.value === 'all' ? 'There are no expired or expiring documents that require renewal attention.' : 'Check back later for renewal updates.'), 1)
                      ]))
                    : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredAlerts.value, (alert) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: alert.id,
                    class: "p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition cursor-pointer flex items-start gap-3 group",
                    onClick: ($event: any) => (handleAlertClick(alert))
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 shadow-xs", alert.is_expired ? 'bg-red-100 text-red-600 dark:bg-red-950/70 dark:text-red-400' : 'bg-amber-100 text-amber-600 dark:bg-amber-950/70 dark:text-amber-400'])
                    }, [
                      (alert.is_expired)
                        ? (_openBlock(), _createBlock(_unref(AlertTriangle), {
                            key: 0,
                            class: "w-4 h-4"
                          }))
                        : (_openBlock(), _createBlock(_unref(Clock), {
                            key: 1,
                            class: "w-4 h-4"
                          }))
                    ], 2),
                    _createElementVNode("div", _hoisted_18, [
                      _createElementVNode("div", _hoisted_19, [
                        _createElementVNode("p", _hoisted_20, _toDisplayString(alert.document_name), 1),
                        _createElementVNode("span", {
                          class: _normalizeClass(["px-1.5 py-0.2 rounded-full text-[10px] font-extrabold uppercase tracking-wider flex-shrink-0", alert.is_expired ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'])
                        }, _toDisplayString(alert.is_expired ? 'Expired' : 'Expiring Soon'), 3)
                      ]),
                      (alert.employee_name && alert.employee_name !== 'My Document')
                        ? (_openBlock(), _createElementBlock("p", _hoisted_21, [
                            _createVNode(_unref(User), { class: "w-3 h-3 text-gray-400 flex-shrink-0" }),
                            _createElementVNode("span", null, _toDisplayString(alert.employee_name), 1),
                            (alert.employee_position)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_22, "• " + _toDisplayString(alert.employee_position), 1))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_23, [
                        _createElementVNode("span", _hoisted_24, _toDisplayString(alert.document_category), 1),
                        _createElementVNode("span", {
                          class: _normalizeClass(["font-semibold whitespace-nowrap", alert.is_expired ? 'text-red-600 dark:text-red-400 font-bold' : 'text-amber-600 dark:text-amber-400'])
                        }, _toDisplayString(formatExpiryDescription(alert)), 3)
                      ])
                    ])
                  ], 8, _hoisted_17))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_25, [
                _createElementVNode("button", {
                  type: "button",
                  class: "text-[11px] font-bold text-secondary dark:text-secondary-dark-text hover:underline flex items-center gap-1",
                  onClick: handleNavigateToDocuments
                }, [
                  _createElementVNode("span", null, _toDisplayString(isStaffOnly.value ? 'View My Documents' : 'Manage Staff Documents'), 1),
                  _createVNode(_unref(ChevronRight), { class: "w-3 h-3" })
                ]),
                _createElementVNode("span", _hoisted_26, _toDisplayString(_unref(store).summary.total_alerts) + " total alert" + _toDisplayString(_unref(store).summary.total_alerts === 1 ? '' : 's'), 1)
              ])
            ], 512))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})